import React, { useEffect, useState, useCallback } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { isNativeApp } from '@/lib/workspaces';
import { APP_ICONS } from '@/lib/appIcons';

// Public mobile entrance for TFS Connect. Entrance buttons ONLY choose a
// destination — they never assign roles or grant permissions. Server-side auth
// on /login and /rout remains the sole authority. No business data is loaded here.

const ASSET = '/tfs-connect';

// Centralized destination map so TEAM / Partners can be wired later without
// touching the layout. type 'route' navigates to an existing verified route;
// type 'soon' shows a localized Coming Soon message.
const DESTINATIONS = {
  team: { type: 'route', to: '/team' },
  management: { type: 'route', to: '/login' },   // existing Total-E management login
  partners: { type: 'route', to: '/partners' },
  customers: { type: 'route', to: '/rout' },      // existing ROUT Client Portal customer login
};

const STRINGS = {
  en: {
    welcome: 'WELCOME',
    subtitle: 'Choose your entrance',
    team: 'T.E.A.M.',
    management: 'Management',
    partners: 'Partners',
    customers: 'Customers',
    division: 'A Total Facility Solutions, Inc. division',
    rights: 'all rights reserved',
    soonTitle: 'Coming soon',
    soonTeam: 'The TEAM experience is on its way. Please check back shortly.',
    soonPartners: 'The Partners experience is on its way. Please check back shortly.',
    close: 'Close',
    langLabel: 'Language',
  },
  es: {
    welcome: 'BIENVENIDO',
    subtitle: 'Elige tu acceso',
    team: 'T.E.A.M.',
    management: 'Administración',
    partners: 'Colaboradores',
    customers: 'Clientes',
    division: 'Una división de Total Facility Solutions, Inc.',
    rights: 'todos los derechos reservados',
    soonTitle: 'Próximamente',
    soonTeam: 'La experiencia TEAM estará disponible pronto. Vuelve en breve.',
    soonPartners: 'La experiencia de Socios estará disponible pronto. Vuelve en breve.',
    close: 'Cerrar',
    langLabel: 'Idioma',
  },
};

const LANG_KEY = 'tfsconnect_lang';
const detectLang = () => {
  try {
    const saved = localStorage.getItem(LANG_KEY);
    if (saved === 'en' || saved === 'es') return saved;
    const nav = (navigator.language || navigator.userLanguage || 'en').toLowerCase();
    return nav.startsWith('es') ? 'es' : 'en';
  } catch { return 'en'; }
};

const ENTRANCES = [
  { key: 'team', icon: APP_ICONS.team, iconAlt: 'TEAM' },
  { key: 'management', icon: APP_ICONS.management, iconAlt: 'Total-E' },
  { key: 'customers', icon: APP_ICONS.customers, iconAlt: 'ROUT Client Portal' },
];

// Height-responsive sizing. clamp() shrinks on shorter screens while the min
// keeps touch targets comfortably above 44px (icon min 2.75rem + vertical
// padding → pill ≥ ~60px tall).
// Use dvh (dynamic viewport height) so scaling matches what is ACTUALLY visible
// with the mobile browser bar shown — vh resolves to the large (chrome-hidden)
// viewport and would overflow at scroll position zero on real phones.
const ICON_SIZE = 'clamp(2.75rem, 7.2dvh, 4rem)';
const PILL_PAD_Y = 'clamp(0.5rem, 1.6dvh, 1rem)';
const LABEL_SIZE = 'clamp(1.25rem, 3.4dvh, 1.5rem)';

const EntranceButton = ({ item, label, onActivate }) => {
  const [imgOk, setImgOk] = useState(true);
  return (
    <button
      type="button"
      onClick={() => onActivate(item.key)}
      data-testid={`tfsconnect-btn-${item.key}`}
      style={{ paddingTop: PILL_PAD_Y, paddingBottom: PILL_PAD_Y }}
      className="group relative flex w-full items-center gap-4 overflow-hidden rounded-[26px] border border-white/80 bg-gradient-to-b from-[#fdfdfc] via-[#f1f2f4] to-[#dde0e5] px-4 text-left shadow-[0_14px_28px_-6px_rgba(3,12,28,0.58),0_5px_10px_rgba(3,12,28,0.32),inset_0_2px_0_rgba(255,255,255,1),inset_0_-3px_5px_rgba(14,42,71,0.16)] transition-all duration-150 active:translate-y-px active:shadow-[0_6px_12px_-3px_rgba(3,12,28,0.46),inset_0_1px_0_rgba(255,255,255,0.9)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#8FC93A] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0a1e3a] motion-reduce:transition-none motion-reduce:active:translate-y-0"
    >
      <span aria-hidden="true" className="pointer-events-none absolute inset-x-0 top-0 h-[46%] rounded-t-[26px] bg-gradient-to-b from-white/65 via-white/18 to-transparent" />
      {/* Original transparent artwork floats above the pill — no wrapper frame,
          border, tile, clip, or rounded corners. object-contain preserves the
          complete image and its native aspect ratio. */}
      <span className="relative z-10 flex flex-none items-center justify-center" style={{ height: ICON_SIZE, width: ICON_SIZE }}>
        {imgOk ? (
          <img src={item.icon} alt={item.iconAlt} onError={() => setImgOk(false)}
            className="h-full w-full object-contain" draggable={false} />
        ) : (
          <span className="flex h-full w-full items-center justify-center rounded-2xl bg-[#0E2A47] text-xs font-bold text-white">{label.slice(0, 2)}</span>
        )}
      </span>
      <span className="relative z-10 min-w-0 flex-1 truncate font-extrabold leading-none tracking-tight text-[#0E2A47]" style={{ fontFamily: "'Nunito', system-ui, sans-serif", fontSize: LABEL_SIZE }}>{label}</span>
      <ChevronRight className="relative z-10 h-7 w-7 flex-none text-[#0E2A47]/70 transition-transform duration-150 group-hover:translate-x-0.5 motion-reduce:transition-none" strokeWidth={3} />
    </button>
  );
};

const TFSConnect = () => {
  const navigate = useNavigate();
  const [lang, setLang] = useState(detectLang);
  const [soon, setSoon] = useState(null); // 'team' | 'partners' | null
  const [footerOk, setFooterOk] = useState(true);
  const t = STRINGS[lang];

  useEffect(() => { try { localStorage.setItem(LANG_KEY, lang); } catch { /* noop */ } }, [lang]);
  useEffect(() => { document.documentElement.lang = lang; }, [lang]);

  const activate = useCallback((key) => {
    const dest = DESTINATIONS[key];
    if (!dest) return;
    if (dest.type === 'route') navigate(dest.to);
    else setSoon(key);
  }, [navigate]);

  return (
    <div
      className="fixed inset-0 overflow-y-auto bg-[#0a1e3a] text-white"
      style={{
        backgroundImage: `url(${ASSET}/bg.png)`,
        backgroundSize: 'cover',
        backgroundPosition: 'top center',
        backgroundRepeat: 'no-repeat',
        backgroundColor: '#0a1e3a',
        fontFamily: "'Nunito', system-ui, -apple-system, Segoe UI, sans-serif",
      }}
      data-testid="tfsconnect-entrance"
    >
      {/* Fills the dynamic viewport (accounts for browser bars). Portrait content
          fits within 100dvh via clamp scaling; landscape / large text overflow
          scrolls on the parent. Footer stays in normal flow (mt-auto). */}
      <div
        className="mx-auto flex w-full max-w-md flex-col px-6"
        style={{
          minHeight: '100dvh',
          paddingTop: 'max(env(safe-area-inset-top), 0.65rem)',
          paddingBottom: 'max(env(safe-area-inset-bottom), 0.9rem)',
        }}
      >
        {/* Language toggle */}
        <div className="flex justify-end" role="group" aria-label={t.langLabel}>
          <div className="inline-flex overflow-hidden rounded-full bg-white/10 ring-1 ring-white/20 backdrop-blur-sm">
            {['en', 'es'].map((l) => (
              <button key={l} type="button" onClick={() => setLang(l)} data-testid={`tfsconnect-lang-${l}`}
                aria-pressed={lang === l}
                className={`px-3 py-1 text-xs font-bold transition-colors ${lang === l ? 'bg-white text-[#0E2A47]' : 'text-white/80'}`}>
                {l.toUpperCase()}
              </button>
            ))}
          </div>
        </div>

        {/* Brand header */}
        <header className="flex flex-col items-center text-center" style={{ marginTop: 'clamp(0.4rem, 1.4dvh, 0.75rem)' }}>
          <img src={`${ASSET}/tfsi.webp`} alt="Total Facility Solutions, Inc." className="w-auto object-contain" style={{ height: 'clamp(2.25rem, 7.2dvh, 3.5rem)' }} draggable={false} />
          <h1 className="font-black leading-none tracking-[0.02em]" style={{ fontFamily: "'Nunito', system-ui, sans-serif", marginTop: 'clamp(0.6rem, 2.8dvh, 1.5rem)', fontSize: 'clamp(2rem, 6.8dvh, 2.75rem)' }} data-testid="tfsconnect-welcome">{t.welcome}</h1>
          <p className="font-semibold text-white/60" style={{ fontFamily: "'Nunito', system-ui, sans-serif", marginTop: 'clamp(0.3rem, 1dvh, 0.625rem)', fontSize: 'clamp(0.9rem, 2.1dvh, 1.05rem)' }}>{t.subtitle}</p>
        </header>

        {/* Entrances */}
        <main className="flex flex-col" style={{ marginTop: 'clamp(0.85rem, 2.8dvh, 1.75rem)', gap: 'clamp(0.5rem, 1.6dvh, 1rem)' }} data-testid="tfsconnect-entrances">
          {ENTRANCES.map((item) => (
            <EntranceButton key={item.key} item={item} label={t[item.key]} onActivate={activate} />
          ))}
        </main>

        {/* Footer — transparent Total Technologies logo (the "A Total Facility
            Solutions, Inc. division" wording is already part of this image, so no
            separate text line). Sits directly on the wallpaper, no container. */}
        <footer className="mt-auto flex flex-col items-center text-center" style={{ paddingTop: 'clamp(0.75rem, 3dvh, 2rem)' }}>
          {footerOk ? (
            <img src={`${ASSET}/total-technologies.png`} alt="Total Technologies — A Total Facility Solutions, Inc. division"
              onError={() => setFooterOk(false)} className="mx-auto w-auto object-contain" style={{ height: 'clamp(3.125rem, 7.8125dvh, 4.6875rem)' }} draggable={false} />
          ) : (
            <div className="text-lg font-bold tracking-tight text-white/90">Total Technologies</div>
          )}
          <p className="text-white/50" style={{ marginTop: 'clamp(0.4rem, 1.1dvh, 0.6rem)', fontSize: 'clamp(0.7rem, 1.5dvh, 0.8rem)' }} data-testid="tfsconnect-copyright">© 2026 Total Facility Solutions, Inc.</p>
        </footer>
      </div>

      {/* Localized Coming Soon */}
      {soon && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-6" data-testid="tfsconnect-soon-modal"
          onClick={() => setSoon(null)}>
          <div className="w-full max-w-sm rounded-3xl bg-white p-6 text-center text-[#0E2A47] shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center overflow-hidden rounded-2xl">
              <img src={soon === 'team' ? `${ASSET}/team.webp` : `${ASSET}/partners.png`} alt="" className="h-14 w-14 object-contain"
                onError={(e) => { e.currentTarget.style.display = 'none'; }} />
            </div>
            <h2 className="text-xl font-extrabold">{t.soonTitle}</h2>
            <p className="mt-2 text-sm text-slate-600">{soon === 'team' ? t.soonTeam : t.soonPartners}</p>
            <button type="button" onClick={() => setSoon(null)} data-testid="tfsconnect-soon-close"
              className="mt-5 w-full rounded-full bg-[#0E2A47] px-4 py-3 text-sm font-bold text-white">{t.close}</button>
          </div>
        </div>
      )}
    </div>
  );
};

// Phone-only gate for the main entry route. Desktop/tablet and authenticated
// users keep the existing behavior (redirect to their default route). Uses
// responsive media queries only (never user-agent): portrait phone widths, plus
// short landscape phones — real desktops/tablets have viewport height > 480px so
// they are unaffected.
const PHONE_MQ = '(max-width: 768px), (orientation: landscape) and (max-height: 480px) and (max-width: 932px)';

export const MobileEntranceGate = ({ user, defaultRoute }) => {
  const [isPhone, setIsPhone] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia(PHONE_MQ).matches);
  useEffect(() => {
    const mq = window.matchMedia(PHONE_MQ);
    const h = (e) => setIsPhone(e.matches);
    mq.addEventListener('change', h);
    return () => mq.removeEventListener('change', h);
  }, []);
  if (user) return <Navigate to={defaultRoute} replace />;
  // Installed TEAM app (Capacitor): skip the public ecosystem entrance and go
  // straight to the TEAM login/hub resolved by defaultRouteFor.
  if (isNativeApp()) return <Navigate to={defaultRoute} replace />;
  if (!isPhone) return <Navigate to={defaultRoute} replace />;
  return <TFSConnect />;
};

export default TFSConnect;
